import * as Sentry from "@sentry/react"

import { PRELOAD_ERROR_RELOAD_KEY, shouldReloadOnPreloadError, type PreloadErrorReloadEntry } from "@/lib/preload-error"

/**
 * Window wiring for stale-chunk recovery. After a deploy, Cloudflare Pages swaps all hashed
 * chunks; an already-open tab lazy-loading an old chunk 404s and Vite fires `vite:preloadError`.
 * One reload fetches the new index.html and self-heals. The pure decision (loop guard, /print
 * exemption) lives in `preload-error.ts`.
 *
 * IMPORTANT: `preventDefault()` is called ONLY on the immediate-reload path. A prevented event
 * makes Vite's preload helper resolve the module promise as `undefined`, which crashes
 * `lazyRouteComponent` with a misleading TypeError — so when the guard suppresses, we let the
 * event default-throw and the original error reach the router boundary (or TanStack's own
 * one-shot chunk reload) intact.
 */

let installed = false

/** `ok: false` = sessionStorage unusable → fail CLOSED (never reload; a loop would be unguarded). */
function readPreviousEntry(): { ok: true; entry: PreloadErrorReloadEntry | null } | { ok: false } {
	try {
		const raw = window.sessionStorage.getItem(PRELOAD_ERROR_RELOAD_KEY)
		if (!raw) return { ok: true, entry: null }
		const parsed: unknown = JSON.parse(raw)
		if (typeof parsed !== "object" || parsed === null) return { ok: true, entry: null }
		const entry = parsed as Partial<PreloadErrorReloadEntry>
		if (typeof entry.message !== "string" || typeof entry.at !== "number") return { ok: true, entry: null }
		return { ok: true, entry: { message: entry.message, at: entry.at } }
	} catch {
		return { ok: false }
	}
}

export function installPreloadErrorReload(): void {
	if (installed) return
	installed = true

	window.addEventListener("vite:preloadError", (event) => {
		const payload = event.payload
		const message = payload instanceof Error ? payload.message : String(payload)
		const previous = readPreviousEntry()
		const reload =
			previous.ok &&
			shouldReloadOnPreloadError({
				pathname: window.location.pathname,
				message,
				now: Date.now(),
				previous: previous.entry,
			})

		// Best-effort telemetry; never await a flush — recovery must not wait on Sentry.
		Sentry.withScope((scope) => {
			scope.setTag("source", "vite-preload-error")
			scope.setTag("preload.action", reload ? "reload" : "suppressed")
			scope.setLevel(reload ? "warning" : "error") // a suppressed repeat is the interesting signal
			Sentry.captureException(payload)
		})

		if (!reload) return // no preventDefault: let the original error propagate intact

		event.preventDefault()
		try {
			// Write BEFORE reloading so a reload that dies mid-flight still arms the guard.
			window.sessionStorage.setItem(PRELOAD_ERROR_RELOAD_KEY, JSON.stringify({ message, at: Date.now() } satisfies PreloadErrorReloadEntry))
		} catch {
			// Unreachable in practice (read succeeded above); reload regardless.
		}
		window.location.reload()
	})
}
